import React, { useId } from 'react';
import { Smartphone } from 'lucide-react';

// The ring and the band drawn as vectors, the ring in the finish it was sold in.
// The finish is read from the name the ring advertises, the way the app reads it
// (ring_variants.dart): longest prefix first, so PRBC is ceramic black and not
// metal black. A ring whose name says nothing (R0, R1, or none) looks like the
// app's own product picture, polished silver, without claiming a finish.

const RING_FINISHES = [
  ['PRBC', 'ceramic_black', 'device.material.ceramic', 'device.color.black'],
  ['PRRM', 'rose_matte', 'device.material.metal', 'device.color.rose_matte'],
  ['PRBS', 'brushed_silver', 'device.material.metal', 'device.color.brushed_silver'],
  ['PRSB', 'stealth_black', 'device.material.metal', 'device.color.stealth_black'],
  ['PRB', 'metal_black', 'device.material.metal', 'device.color.black'],
  ['PRS', 'metal_silver', 'device.material.metal', 'device.color.silver'],
  ['PRG', 'metal_gold', 'device.material.metal', 'device.color.gold'],
  ['PRP', 'ceramic_pink', 'device.material.ceramic', 'device.color.pink'],
  ['PRW', 'ceramic_white', 'device.material.ceramic', 'device.color.white'],
];

export const FINISHES = RING_FINISHES.map(([, finish, material, color]) => ({ finish, material, color }));

// What a device looks like: a ring and its finish, the band, or a phone for
// readings that came from Apple Health or Health Connect.
export function deviceLook(origin, name = null) {
  if (origin === 'b01') return { kind: 'band', finish: 'band', known: true, material: null, color: null };
  if (origin !== 'r01') return { kind: 'phone', finish: null, known: false, material: null, color: null };
  const upper = typeof name === 'string' ? name.toUpperCase() : '';
  const found = RING_FINISHES.find(([prefix]) => upper.startsWith(prefix));
  if (!found) return { kind: 'ring', finish: 'metal_silver', known: false, material: null, color: null };
  return { kind: 'ring', finish: found[1], known: true, material: found[2], color: found[3] };
}

// "Ceramic · Pink" in the app's own words, or null when the ring never said.
export function finishLabel(look, as) {
  if (!look.known || look.material === null) return null;
  return `${as(look.material)} · ${as(look.color)}`;
}

// Each finish's colours as they run over the band from its left end to its right,
// sampled from the app's product photo of that ring (lib/images/rings), then the
// front rim's light and dark. Brushed finishes also get fine lines.
const PALETTES = {
  metal_silver: { band: ['#a8a8a8', '#c5c3c1', '#b5b5b5', '#7f7f7f', '#d2d2d2', '#cbcbcb', '#c6c6c6', '#9f9e9e'], rim: ['#f4f4f4', '#8e8e8e'] },
  metal_gold: { band: ['#bda779', '#d8caa5', '#d6bc87', '#9d8457', '#e6d3a3', '#e1c894', '#e5d09e', '#b59b6c'], rim: ['#f6e8c3', '#9d8457'] },
  metal_black: { band: ['#4b4b4b', '#888888', '#535353', '#313130', '#737272', '#575757', '#686868', '#383838'], rim: ['#a2a2a2', '#2a2a2a'] },
  ceramic_black: { band: ['#646464', '#7c7c7c', '#646464', '#515151', '#6b6b6b', '#5f5f5f', '#646564', '#4e4e4e'], rim: ['#8e8e8e', '#353535'] },
  ceramic_pink: { band: ['#eac5ca', '#f4d7db', '#ecc8cc', '#e1babe', '#f5d6da', '#f1cfd3', '#f3d5d8', '#e1b9bd'], rim: ['#fcecee', '#d4aab0'] },
  ceramic_white: { band: ['#e7e7e8', '#d2d1cf', '#dbdbdc', '#d1d1d1', '#e7e7e8', '#e0e0e1', '#bfc0c0', '#c5c5c5'], rim: ['#ffffff', '#b8b8b8'] },
  rose_matte: { band: ['#ca9d98', '#e8bdb9', '#d2a7a3', '#b18581', '#e6b9b5', '#daafab', '#ecc1bd', '#bc908c'], rim: ['#f4d5d1', '#ad817d'] },
  brushed_silver: { band: ['#b1b1b1', '#d6d6d6', '#a7a7a7', '#8b8b8b', '#cecece', '#cacaca', '#d0d0d0', '#939393'], rim: ['#f0f0f0', '#8b8b8b'], brushed: true },
  stealth_black: { band: ['#3f4040', '#727273', '#515151', '#2e2e2d', '#606060', '#545454', '#565656', '#303130'], rim: ['#6e6e6e', '#262626'], brushed: true },
};

// The ring as the app's product photos show it, measured from them: its long
// axis runs from upper left to lower right at 29 degrees, the opening faces the
// viewer's lower left and the band shows on the upper right. The front rim and
// the back rim are two ellipses a band's width apart; the band's outside fills
// the space between them, and through both openings one sees through the ring.
const TILT = 29;
const FRONT = { cx: 45.5, cy: 58.7, rx: 43.4, ry: 25.1 };
const AXIS = [10.2, -17.6];
const WALL = 2.3;

const RAD = Math.PI / 180;
function turned([x, y]) {
  const cos = Math.cos(TILT * RAD);
  const sin = Math.sin(TILT * RAD);
  return [x * cos - y * sin, x * sin + y * cos];
}
function onEllipse(shape, t) {
  const [x, y] = turned([shape.rx * Math.cos(t), shape.ry * Math.sin(t)]);
  return [shape.cx + x, shape.cy + y];
}
function shifted(shape, [dx, dy]) {
  return { ...shape, cx: shape.cx + dx, cy: shape.cy + dy };
}
function inner(shape) {
  return { ...shape, rx: shape.rx - WALL, ry: shape.ry - WALL * (shape.ry / shape.rx) };
}

const BACK = shifted(FRONT, AXIS);
const FRONT_HOLE = inner(FRONT);
const BACK_HOLE = inner(BACK);
// Where the band's outline runs straight from the front rim to the back rim.
const EDGE_T = (() => {
  const cos = Math.cos(-TILT * RAD);
  const sin = Math.sin(-TILT * RAD);
  const dx = AXIS[0] * cos - AXIS[1] * sin;
  const dy = AXIS[0] * sin + AXIS[1] * cos;
  return Math.atan2(-FRONT.ry * dx, FRONT.rx * dy);
})();
const SIDES = [onEllipse(FRONT, EDGE_T), onEllipse(BACK, EDGE_T), onEllipse(BACK, EDGE_T + Math.PI), onEllipse(FRONT, EDGE_T + Math.PI)]
  .map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
// The colours run along the ring's long axis, end to end, as sampled from the photos.
const LIGHT = (() => {
  const centre = [(FRONT.cx + BACK.cx) / 2, (FRONT.cy + BACK.cy) / 2];
  const along = [Math.cos(TILT * RAD), Math.sin(TILT * RAD)];
  return {
    from: [centre[0] - along[0] * FRONT.rx, centre[1] - along[1] * FRONT.rx],
    to: [centre[0] + along[0] * FRONT.rx, centre[1] + along[1] * FRONT.rx],
  };
})();
const STOPS = PALETTES.metal_silver.band.map((_, index, all) => index / (all.length - 1));
// The three gold sensor contacts on the inside wall, at the left of the opening.
const CONTACTS = [170, 181, 192].map((degrees) => {
  const [x, y] = onEllipse(FRONT_HOLE, degrees * RAD);
  return [x + (FRONT.cx - x) * 0.13, y + (FRONT.cy - y) * 0.13];
});

function Ellipse({ shape, ...rest }) {
  return <ellipse cx={shape.cx} cy={shape.cy} rx={shape.rx} ry={shape.ry} transform={`rotate(${TILT} ${shape.cx} ${shape.cy})`} {...rest} />;
}

export function RingArt({ finish = 'metal_silver', size = 48, title = null }) {
  const id = useId().replace(/:/g, '');
  const palette = PALETTES[finish] || PALETTES.metal_silver;
  const band = `${id}-band`;
  const rim = `${id}-rim`;
  const wall = `${id}-wall`;
  const through = `${id}-through`;
  const hole = `${id}-hole`;
  const lines = `${id}-lines`;
  const outside = `${id}-outside`;
  return (
    <svg className="xo-wear-art is-ring" viewBox="0 0 100 100" width={size} height={size} role={title ? 'img' : undefined}
      aria-label={title || undefined} aria-hidden={title ? undefined : 'true'}>
      <defs>
        <linearGradient id={band} gradientUnits="userSpaceOnUse" x1={LIGHT.from[0]} y1={LIGHT.from[1]} x2={LIGHT.to[0]} y2={LIGHT.to[1]}>
          {palette.band.map((color, index) => <stop key={index} offset={STOPS[index]} stopColor={color} />)}
        </linearGradient>
        <linearGradient id={rim} gradientUnits="userSpaceOnUse" x1={LIGHT.from[0]} y1={LIGHT.from[1]} x2={LIGHT.to[0]} y2={LIGHT.to[1]}>
          <stop offset="0" stopColor={palette.rim[0]} /><stop offset=".55" stopColor={palette.rim[1]} /><stop offset="1" stopColor={palette.rim[0]} />
        </linearGradient>
        <linearGradient id={wall} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#26272a" /><stop offset=".5" stopColor="#101112" /><stop offset="1" stopColor="#1d1e21" />
        </linearGradient>
        <clipPath id={hole}><Ellipse shape={FRONT_HOLE} /></clipPath>
        <clipPath id={outside}>
          <Ellipse shape={BACK} /><polygon points={SIDES} />
        </clipPath>
        <mask id={through} maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="100">
          <rect width="100" height="100" fill="#fff" />
          <g clipPath={`url(#${hole})`}><Ellipse shape={BACK_HOLE} fill="#000" /></g>
        </mask>
        {palette.brushed && (
          <pattern id={lines} width="3" height="1.2" patternUnits="userSpaceOnUse" patternTransform={`rotate(${TILT - 45})`}>
            <rect width="3" height=".45" fill="#fff" opacity=".18" />
          </pattern>
        )}
      </defs>
      <g mask={`url(#${through})`}>
        <Ellipse shape={BACK} fill={`url(#${band})`} />
        <polygon points={SIDES} fill={`url(#${band})`} />
        {palette.brushed && <g clipPath={`url(#${outside})`}><rect width="100" height="100" fill={`url(#${lines})`} /></g>}
        <Ellipse shape={FRONT} fill={`url(#${rim})`} />
        <Ellipse shape={FRONT_HOLE} fill={`url(#${wall})`} />
        {CONTACTS.map(([x, y]) => <circle key={`${x}`} cx={x} cy={y} r="1.5" fill="#d8ab4f" />)}
      </g>
      <g clipPath={`url(#${hole})`}>
        <Ellipse shape={BACK_HOLE} fill="none" stroke="#303134" strokeWidth="1.6" />
      </g>
      <Ellipse shape={FRONT_HOLE} fill="none" stroke="#050506" strokeWidth=".6" opacity=".8" />
    </svg>
  );
}

// The band as its product photo shows it (lib/images/APL_Screenless.png): a
// narrow black strap, a third as wide as it is long, perforated in four columns,
// four sensor lights down the middle, the gunmetal frame's rails down both sides
// and the plate with the Phenome mark across its lower end.
const STRAP = { x: 33.5, y: 2, width: 33, height: 96 };
const HOLE_COLUMNS = [0.1, 0.36, 0.64, 0.9].map((share) => STRAP.x + STRAP.width * share);
const HOLES = [];
for (let y = 5; y <= 95; y += 4.8) {
  if (y > 70 && y < 83) continue;
  for (const x of HOLE_COLUMNS) HOLES.push([x, y]);
}
const LIGHTS = [34, 46, 58, 70];

export function BandArt({ size = 48, title = null }) {
  const id = useId().replace(/:/g, '');
  const strap = `${id}-strap`;
  const frame = `${id}-frame`;
  return (
    <svg className="xo-wear-art is-band" viewBox="0 0 100 100" width={size} height={size} role={title ? 'img' : undefined}
      aria-label={title || undefined} aria-hidden={title ? undefined : 'true'}>
      <defs>
        <linearGradient id={strap} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#0e0f10" /><stop offset=".25" stopColor="#222326" /><stop offset=".5" stopColor="#2a2b2e" />
          <stop offset=".75" stopColor="#212225" /><stop offset="1" stopColor="#0d0e0f" />
        </linearGradient>
        <linearGradient id={frame} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#2a2c30" /><stop offset=".5" stopColor="#6a6d73" /><stop offset="1" stopColor="#303237" />
        </linearGradient>
      </defs>
      <rect x={STRAP.x} y={STRAP.y} width={STRAP.width} height={STRAP.height} rx="4" fill={`url(#${strap})`} />
      {HOLES.map(([x, y]) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.35" fill="#060607" />)}
      {LIGHTS.map((y) => <circle key={y} cx="50" cy={y} r="1.6" fill="#e3e5ea" />)}
      <rect x={STRAP.x - 2.4} y="17" width="3" height="64" rx="1.5" fill={`url(#${frame})`} />
      <rect x={STRAP.x + STRAP.width - 0.6} y="17" width="3" height="64" rx="1.5" fill={`url(#${frame})`} />
      <rect x={STRAP.x - 2.4} y="72.5" width={STRAP.width + 4.8} height="8" rx="1.8" fill={`url(#${frame})`} />
      <path d="M48.7 74.6h2.5a1.35 1.35 0 0 1 0 2.7h-1.4v1.6" fill="none" stroke="#d5d8de" strokeWidth=".7" strokeLinecap="round" />
    </svg>
  );
}

// A device as it looks: the ring in its finish, the band, or a phone.
export function DeviceArt({ origin, name = null, size = 48, title = null, className = '' }) {
  const look = deviceLook(origin, name);
  return (
    <span className={`xo-device-art-frame is-${look.kind} ${className}`} style={{ width: size, height: size }}>
      {look.kind === 'ring' && <RingArt finish={look.finish} size={size} title={title} />}
      {look.kind === 'band' && <BandArt size={size} title={title} />}
      {look.kind === 'phone' && <Smartphone aria-hidden="true" />}
    </span>
  );
}
